/**
 * useGroupableDrag - Hook for velocity-based drag mode switching
 *
 * Tracks drag velocity to differentiate between reorder and group intent:
 * - Fast drag (moving) = reorder mode
 * - Slow drag + 750ms dwell = group mode with drop zone targeting
 */

import { useDndMonitor } from '@dnd-kit/core';
import { useCallback, useEffect, useRef, useState } from 'react';

import type { DragMoveEvent, DragStartEvent } from '@dnd-kit/core';

import { hapticMedium } from '@/utils';

export type DragMode = 'reorder' | 'group';

export interface DropTarget {
  uuid: string;
  name: string;
  type: 'exercise' | 'superset';
  yCenter: number;
}

export interface UseGroupableDragOptions {
  /** List of potential drop targets with their Y-center positions */
  targets: DropTarget[];
  /** Dwell time threshold in ms before switching to group mode (default: 750) */
  dwellThreshold?: number;
  /** Velocity threshold below which dwell detection begins (default: 50 px/s) */
  velocityThreshold?: number;
  /** Whether groupable drag is enabled (default: true) */
  enabled?: boolean;
}

export interface UseGroupableDragReturn {
  /** Current drag mode: 'reorder' for fast drag, 'group' for dwell targeting */
  dragMode: DragMode;
  /** Whether currently dwelling (stationary for threshold duration) */
  isDwelling: boolean;
  /** The nearest drop target based on Y-center proximity (null if not dwelling) */
  nearestTarget: DropTarget | null;
  /** UUID of the item being dragged (null if not dragging) */
  activeId: string | null;
  /** Whether a drag is currently in progress */
  isDragging: boolean;
}

interface PositionSample {
  y: number;
  /** `performance.now()` reading — monotonic, in-memory only. */
  timestamp: number;
}

/**
 * Stay this still, this long, and a drag switches from reorder to group. A
 * deliberate hold, clear of the pause a hand makes while lining a row up to
 * drop it — 200ms, then 500ms, were both crossed by a normal reorder on a
 * phone. Mobile's `DWELL_MS` is the same figure.
 */
export const DEFAULT_DWELL_THRESHOLD = 750;
const DEFAULT_VELOCITY_THRESHOLD = 50;
const VELOCITY_SAMPLE_WINDOW = 100;
const MAX_POSITION_SAMPLES = 20;

export function useGroupableDrag({
  targets,
  dwellThreshold = DEFAULT_DWELL_THRESHOLD,
  velocityThreshold = DEFAULT_VELOCITY_THRESHOLD,
  enabled = true,
}: UseGroupableDragOptions): UseGroupableDragReturn {
  const [dragMode, setDragMode] = useState<DragMode>('reorder');
  const [isDwelling, setIsDwelling] = useState(false);
  const [nearestTarget, setNearestTarget] = useState<DropTarget | null>(null);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const positionSamples = useRef<PositionSample[]>([]);
  const dwellTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const currentY = useRef<number>(0);

  const clearDwellTimer = useCallback(() => {
    if (dwellTimerRef.current) {
      clearTimeout(dwellTimerRef.current);
      dwellTimerRef.current = null;
    }
  }, []);

  const calculateVelocity = useCallback((): number => {
    const samples = positionSamples.current;
    if (samples.length < 2) return 0;

    const now = performance.now();
    const recentSamples = samples.filter(
      (s) => now - s.timestamp < VELOCITY_SAMPLE_WINDOW
    );

    if (recentSamples.length < 2) return 0;

    const oldest = recentSamples[0];
    const newest = recentSamples[recentSamples.length - 1];
    const deltaY = Math.abs(newest.y - oldest.y);
    const deltaTime = (newest.timestamp - oldest.timestamp) / 1000;

    return deltaTime > 0 ? deltaY / deltaTime : 0;
  }, []);

  const findNearestTarget = useCallback(
    (y: number, excludeId: string | null): DropTarget | null => {
      if (targets.length === 0) return null;

      let nearest: DropTarget | null = null;
      let minDistance = Infinity;

      for (const target of targets) {
        if (target.uuid === excludeId) continue;

        const distance = Math.abs(target.yCenter - y);
        if (distance < minDistance) {
          minDistance = distance;
          nearest = target;
        }
      }

      return nearest;
    },
    [targets]
  );

  const startDwellDetection = useCallback(
    (y: number) => {
      clearDwellTimer();

      dwellTimerRef.current = setTimeout(() => {
        const velocity = calculateVelocity();
        if (velocity < velocityThreshold) {
          setIsDwelling(true);
          setDragMode('group');
          hapticMedium();
          const target = findNearestTarget(y, activeId);
          setNearestTarget(target);
        }
      }, dwellThreshold);
    },
    [
      clearDwellTimer,
      calculateVelocity,
      velocityThreshold,
      dwellThreshold,
      findNearestTarget,
      activeId,
    ]
  );

  const handleDragStart = useCallback(
    (event: DragStartEvent) => {
      if (!enabled) return;

      setActiveId(String(event.active.id));
      setIsDragging(true);
      setDragMode('reorder');
      setIsDwelling(false);
      setNearestTarget(null);
      positionSamples.current = [];
    },
    [enabled]
  );

  const handleDragMove = useCallback(
    (event: DragMoveEvent) => {
      if (!enabled || !event.active) return;

      // Use previous Y as fallback since 0 is a valid coordinate at viewport top
      const y =
        event.active.rect.current.translated?.top ?? currentY.current ?? 0;
      const now = performance.now();

      currentY.current = y;
      positionSamples.current.push({ y, timestamp: now });

      // Filter old samples and cap at max length to prevent unbounded growth
      positionSamples.current = positionSamples.current
        .filter((s) => now - s.timestamp < VELOCITY_SAMPLE_WINDOW * 2)
        .slice(-MAX_POSITION_SAMPLES);

      const velocity = calculateVelocity();

      if (velocity >= velocityThreshold) {
        clearDwellTimer();
        if (dragMode === 'group') {
          setDragMode('reorder');
          setIsDwelling(false);
          setNearestTarget(null);
        }
        // Stop here: `isDwelling` below is this render's value, still true on
        // the move that ends a dwell, and would re-set the target just cleared.
        return;
      }

      if (!isDwelling && dwellTimerRef.current === null) {
        startDwellDetection(y);
      }

      if (isDwelling) {
        const target = findNearestTarget(y, activeId);
        if (target?.uuid !== nearestTarget?.uuid) {
          setNearestTarget(target);
        }
      }
    },
    [
      enabled,
      calculateVelocity,
      velocityThreshold,
      clearDwellTimer,
      dragMode,
      isDwelling,
      startDwellDetection,
      findNearestTarget,
      activeId,
      nearestTarget?.uuid,
    ]
  );

  const handleDragEnd = useCallback(() => {
    clearDwellTimer();
    setActiveId(null);
    setIsDragging(false);
    setDragMode('reorder');
    setIsDwelling(false);
    setNearestTarget(null);
    positionSamples.current = [];
  }, [clearDwellTimer]);

  const handleDragCancel = useCallback(() => {
    clearDwellTimer();
    setActiveId(null);
    setIsDragging(false);
    setDragMode('reorder');
    setIsDwelling(false);
    setNearestTarget(null);
    positionSamples.current = [];
  }, [clearDwellTimer]);

  useDndMonitor({
    onDragStart: handleDragStart,
    onDragMove: handleDragMove,
    onDragEnd: handleDragEnd,
    onDragCancel: handleDragCancel,
  });

  useEffect(() => {
    return () => {
      clearDwellTimer();
    };
  }, [clearDwellTimer]);

  return {
    dragMode,
    isDwelling,
    nearestTarget,
    activeId,
    isDragging,
  };
}

export default useGroupableDrag;
